class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
    document.dispatchEvent(new CustomEvent('facets:activeFacetsRendered'));
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this.minSlider = this.querySelector('.facet-price-range__input--min');
    this.maxSlider = this.querySelector('.facet-price-range__input--max');
    this.minText = this.querySelector('.facet-price-range__text-input--min');
    this.maxText = this.querySelector('.facet-price-range__text-input--max');
    this.groupEl = this.querySelector('.facet-price-range__range-group');

    if (!this.minSlider || !this.maxSlider || !this.minText || !this.maxText) return;

    this.rangeMin = this.parseNum(this.minSlider.min, this.parseNum(this.dataset.rangeMin, 0));
    this.rangeMax = this.parseNum(this.maxSlider.max, this.parseNum(this.dataset.rangeMax, 0));
    if (this.rangeMax < this.rangeMin) this.rangeMax = this.rangeMin;

    const hasFilter = this.minText.value.trim() !== '' || this.maxText.value.trim() !== '';
    if (hasFilter) {
      this.syncSlidersFromText(false);
    } else {
      this.minSlider.value = String(this.rangeMin);
      this.maxSlider.value = String(this.rangeMax);
    }
    this.updateProgress();
    this.setMinAndMaxValues();

    this.onSliderInput = () => {
      let lo = this.parseNum(this.minSlider.value, this.rangeMin);
      let hi = this.parseNum(this.maxSlider.value, this.rangeMax);
      if (lo > hi) {
        if (document.activeElement === this.minSlider) hi = lo;
        else lo = hi;
      }
      lo = this.snapValue(lo, 'down');
      hi = this.snapValue(hi, 'up');
      this.minSlider.value = String(lo);
      this.maxSlider.value = String(hi);

      if (this.isFullRange(lo, hi)) {
        this.minText.value = '';
        this.maxText.value = '';
      } else {
        this.minText.value = this.formatValue(lo, 'down');
        this.maxText.value = this.formatValue(hi, 'up');
      }

      this.updateProgress();
      this.setMinAndMaxValues();
      this.submitForm();
    };

    this.minSlider.addEventListener('input', this.onSliderInput);
    this.maxSlider.addEventListener('input', this.onSliderInput);

    this.onTextChange = (event) => {
      this.adjustToValidValues(event.currentTarget);
      this.syncSlidersFromText(false);
      this.updateProgress();
      this.setMinAndMaxValues();
      this.submitForm();
    };

    this.minText.addEventListener('change', this.onTextChange);
    this.maxText.addEventListener('change', this.onTextChange);

    this.onTextInput = () => {
      this.syncSlidersFromText(false);
      this.updateProgress();
    };

    this.minText.addEventListener('input', this.onTextInput);
    this.maxText.addEventListener('input', this.onTextInput);

    [this.minText, this.maxText].forEach((el) => {
      el.addEventListener('keydown', (event) => this.onKeyDown(event));
    });

    if (this.groupEl) {
      this.groupEl.addEventListener('pointerdown', (event) => {
        if (event.target === this.minSlider || event.target === this.maxSlider) return;
        const rect = this.groupEl.getBoundingClientRect();
        const x = Math.max(0, Math.min(rect.width, event.clientX - rect.left));
        const pct = rect.width ? x / rect.width : 0;
        const span = this.rangeMax - this.rangeMin || 1;
        const rawValue = this.rangeMin + pct * span;
        const v = this.snapValue(rawValue, 'nearest');

        const lo = this.parseNum(this.minSlider.value, this.rangeMin);
        const hi = this.parseNum(this.maxSlider.value, this.rangeMax);

        if (Math.abs(v - lo) <= Math.abs(v - hi)) {
          this.minSlider.value = String(v);
        } else {
          this.maxSlider.value = String(v);
        }
        this.onSliderInput();
        try {
          event.preventDefault();
        } catch (_) {}
      });
    }
  }

  parseNum(value, fallback = NaN) {
    if (value == null || value === '') return fallback;
    const n = parseFloat(String(value).replace(/\s/g, '').replace(',', '.'));
    return Number.isFinite(n) ? n : fallback;
  }

  clamp(value) {
    return Math.max(this.rangeMin, Math.min(this.rangeMax, value));
  }

  snapValue(value, direction = 'nearest') {
    let snapped;
    if (direction === 'up') snapped = Math.ceil(value);
    else if (direction === 'down') snapped = Math.floor(value);
    else snapped = Math.round(value);
    return this.clamp(snapped);
  }

  formatValue(value, direction = 'nearest') {
    return String(this.snapValue(value, direction));
  }

  syncSlidersFromText(updateText = false) {
    const parsedLo = this.parseNum(this.minText.value, this.rangeMin);
    const parsedHi = this.parseNum(this.maxText.value, this.rangeMax);
    let lo = this.clamp(Number.isFinite(parsedLo) ? parsedLo : this.rangeMin);
    let hi = this.clamp(Number.isFinite(parsedHi) ? parsedHi : this.rangeMax);

    if (this.minText.value.trim() === '') lo = this.rangeMin;
    if (this.maxText.value.trim() === '') hi = this.rangeMax;
    if (lo > hi) {
      if (document.activeElement === this.minText) hi = lo;
      else lo = hi;
    }

    lo = this.snapValue(lo, 'down');
    hi = this.snapValue(hi, 'up');
    this.minSlider.value = String(lo);
    this.maxSlider.value = String(hi);

    if (updateText) {
      this.minText.value = this.formatValue(lo, 'down');
      this.maxText.value = this.formatValue(hi, 'up');
    }
  }

  isFullRange(lo, hi) {
    return lo <= this.rangeMin && hi >= this.rangeMax;
  }

  submitForm() {
    const form = this.closest('form');
    if (form) form.dispatchEvent(new Event('input', { bubbles: true }));
  }

  setMinAndMaxValues() {
    if (this.maxText.value) this.minText.setAttribute('data-max', this.maxText.value);
    if (this.minText.value) this.maxText.setAttribute('data-min', this.minText.value);
    if (this.minText.value === '') this.maxText.setAttribute('data-min', 0);
    if (this.maxText.value === '') {
      this.minText.setAttribute('data-max', this.maxText.getAttribute('data-max'));
    }
  }

  adjustToValidValues(input) {
    if (input.value.trim() === '') return;
    const value = this.parseNum(input.value, NaN);
    if (!Number.isFinite(value)) return;
    const min = this.parseNum(input.getAttribute('data-min'), this.rangeMin);
    const max = this.parseNum(input.getAttribute('data-max'), this.rangeMax);
    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }

  updateProgress() {
    if (!this.groupEl) return;
    const lo = this.parseNum(this.minSlider?.value, this.rangeMin);
    const hi = this.parseNum(this.maxSlider?.value, this.rangeMax);
    const span = this.rangeMax - this.rangeMin || 1;
    const minPct = ((lo - this.rangeMin) / span) * 100;
    const maxPct = ((hi - this.rangeMin) / span) * 100;
    this.groupEl.style.setProperty('--range-min', `${Math.max(0, Math.min(100, minPct))}%`);
    this.groupEl.style.setProperty('--range-max', `${Math.max(0, Math.min(100, maxPct))}%`);
  }

  onKeyDown(event) {
    if (event.metaKey || event.ctrlKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
